import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import logo from '../assets/logo.png'

interface RegisterForm {
  username: string
  password: string
  email: string
  first_name: string
  last_name: string
}

export default function RegisterTeacher() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(false)
  const [isRegistrationStatusLoading, setIsRegistrationStatusLoading] = useState(true)
  const { register, handleSubmit, formState: { errors } } = useForm<RegisterForm>()

  useEffect(() => {
    let mounted = true
    api
      .get('/auth/self-registration-status')
      .then((res) => {
        if (!mounted) return
        setIsRegistrationEnabled(Boolean(res.data?.data?.enabled))
      })
      .catch(() => {
        if (!mounted) return
        setIsRegistrationEnabled(false)
      })
      .finally(() => {
        if (!mounted) return
        setIsRegistrationStatusLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  const onSubmit = async (data: RegisterForm) => {
    if (!isRegistrationEnabled) {
      toast.error('Die Selbstregistrierung ist aktuell deaktiviert.')
      return
    }
    setIsSubmitting(true)
    try {
      await api.post('/auth/register', data)
      toast.success('Registrierung eingegangen. Ein Administrator muss Ihr Konto freischalten.')
      navigate('/login')
    } catch (error: any) {
      if (error?.response?.data?.error === 'SELF_REGISTRATION_DISABLED') {
        toast.error('Die Selbstregistrierung ist aktuell deaktiviert.')
        return
      }
      toast.error(error?.response?.data?.message || 'Registrierung fehlgeschlagen')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12 dark:bg-gray-900">
      <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-6 text-center">
          <img src={logo} alt="Lernrakete Logo" className="mx-auto h-20 w-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">Lehrkraft registrieren</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Das Konto wird nach Prüfung durch einen Administrator freigeschaltet.
          </p>
        </div>

        {isRegistrationStatusLoading ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">Lade Registrierungseinstellungen…</p>
        ) : !isRegistrationEnabled ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            Die Selbstregistrierung ist derzeit deaktiviert. Bitte wenden Sie sich an die Schulleitung.
          </div>
        ) : (
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Benutzername</label>
            <input
              {...register('username', { required: 'Benutzername ist erforderlich' })}
              className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              placeholder="Benutzername"
              autoComplete="username"
            />
            {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Passwort</label>
            <input
              {...register('password', {
                required: 'Passwort ist erforderlich',
                minLength: { value: 6, message: 'Mindestens 6 Zeichen' }
              })}
              type="password"
              className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              placeholder="Passwort"
              autoComplete="new-password"
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">E-Mail (optional)</label>
            <input
              {...register('email')}
              type="email"
              className="input-field min-h-[2.25rem] w-full py-2 text-sm"
              placeholder="name@schule.de"
              autoComplete="email"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Vorname (optional)</label>
              <input
                {...register('first_name')}
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
                placeholder="Vorname"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Nachname (optional)</label>
              <input
                {...register('last_name')}
                className="input-field min-h-[2.25rem] w-full py-2 text-sm"
                placeholder="Nachname"
              />
            </div>
          </div>

          <div className="pt-2">
            <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Registrierung läuft...' : 'Registrierung absenden'}
            </Button>
          </div>
        </form>
        )}

        <div className="mt-4 text-center text-sm">
          <Link to="/login" className="text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  )
}
